import { useEffect, useMemo, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { SmoothScrollContext, SmoothScrollValue } from "@/lib/smoothScroll";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ChatWidget } from "@/components/chat/ChatWidget";
import HomePage from "@/pages/HomePage";
import BidRequestPage from "@/pages/BidRequestPage";
import ReviewsPage from "@/pages/ReviewsPage";
import AdminPage from "@/pages/AdminPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import { StickyMobileCta } from "@/components/StickyMobileCta";

function AppShell() {
  const lenisRef = useRef<Lenis | null>(null);
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId: number;
    const raf = (time: number) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const value = useMemo<SmoothScrollValue>(
    () => ({
      scrollTo: (target, immediate = false) => {
        lenisRef.current?.scrollTo(target as never, {
          offset: typeof target === "string" ? -88 : 0,
          duration: immediate ? 0 : 1.3,
        });
      },
    }),
    [],
  );

  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [location.pathname]);

  return (
    <SmoothScrollContext.Provider value={value}>
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/request-a-bid" element={<BidRequestPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/reset/:token" element={<ResetPasswordPage />} />
      </Routes>
      <Footer />
      <div className="h-14 md:hidden" aria-hidden="true" />
      <StickyMobileCta />
      <ChatWidget />
      <Toaster position="top-center" theme="dark" richColors />
    </SmoothScrollContext.Provider>
  );
}

function App() {
  return (
    <div className="grain min-h-screen bg-ink">
      <LanguageProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
